import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Input, Switch, Button, Typography, Space, Image, Divider, Row, Col, Spin, Steps,
} from 'antd';
import { InstagramOutlined, VideoCameraOutlined, SearchOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useQuery } from 'react-query';
import { getProducts } from 'services/product.service';
import { useGenerateSocialPost } from 'hooks/api/useSocialPost';
import { SocialPost } from 'services/social.service';
import Routes from 'config/routes';
import PostPreview from '../Preview';

const { Step } = Steps;

const { Title, Text, Paragraph } = Typography;

interface ProductOption {
  _id: string;
  name: string;
  image: string;
  description: string;
}

const SocialCreate = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [searchName, setSearchName] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductOption | null>(null);
  const [instagramEnabled, setInstagramEnabled] = useState(true);
  const [tiktokEnabled, setTiktokEnabled] = useState(true);
  const [generated, setGenerated] = useState<SocialPost | null>(null);

  const { data: productsData, isFetching } = useQuery(
    ['products-social-search', searchName],
    () => getProducts({ skip: 0, limit: 10, name: searchName || undefined }).then((r) => r.data),
    { enabled: searchName.length > 1, refetchOnWindowFocus: false },
  );

  const { mutate: generate, isLoading } = useGenerateSocialPost();

  const handleSelect = useCallback((product: ProductOption) => {
    setSelectedProduct(product);
    setSearchName('');
  }, []);

  const handleGenerate = useCallback(() => {
    if (!selectedProduct) return;
    generate(
      { productId: selectedProduct._id, instagram: instagramEnabled, tiktok: tiktokEnabled },
      {
        onSuccess: (data) => {
          setGenerated(data);
          setStep(1);
        },
      },
    );
  }, [selectedProduct, instagramEnabled, tiktokEnabled, generate]);

  const products: ProductOption[] = (productsData as any)?.data || [];
  const showDropdown = searchName.length > 1 && products.length > 0;

  return (
    <div style={{ maxWidth: 800, margin: '24px auto' }}>
      <Steps current={step} style={{ marginBottom: 24 }}>
        <Step title='Налаштування' />
        <Step title='Перегляд і публікація' />
      </Steps>

      {step === 0 && (
        <Card>
          <Title level={4}>Новий пост в соцмережі</Title>
          <Space direction='vertical' size='middle' style={{ width: '100%' }}>

            <div style={{ position: 'relative' }}>
              <Input
                prefix={<SearchOutlined />}
                placeholder='Пошук продукту...'
                value={searchName}
                onChange={(e) => { setSearchName(e.target.value); setSelectedProduct(null); }}
                suffix={isFetching ? <Spin size='small' /> : null}
                size='large'
              />
              {showDropdown && (
                <Card
                  size='small'
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    maxHeight: 300,
                    overflowY: 'auto',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                  bodyStyle={{ padding: 0 }}
                >
                  {products.map((p) => (
                    <div
                      key={p._id}
                      role='button'
                      tabIndex={0}
                      onClick={() => handleSelect(p)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSelect(p)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = '#f5f5f5'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ''; }}
                    >
                      {p.image && (
                        <Image
                          src={p.image}
                          width={40}
                          height={40}
                          style={{ objectFit: 'cover', borderRadius: 4 }}
                          preview={false}
                        />
                      )}
                      <Text>{p.name}</Text>
                    </div>
                  ))}
                </Card>
              )}
            </div>

            {selectedProduct && (
              <Card size='small' style={{ background: '#fafafa' }}>
                <Row gutter={16} align='middle'>
                  {selectedProduct.image && (
                    <Col>
                      <Image
                        src={selectedProduct.image}
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 8 }}
                        preview={false}
                      />
                    </Col>
                  )}
                  <Col flex={1}>
                    <Text strong>{selectedProduct.name}</Text>
                    {selectedProduct.description && (
                      <Paragraph
                        type='secondary'
                        ellipsis={{ rows: 2 }}
                        style={{ margin: '4px 0 0' }}
                      >
                        {selectedProduct.description}
                      </Paragraph>
                    )}
                  </Col>
                </Row>
              </Card>
            )}

            <Divider style={{ margin: '4px 0' }} />

            <Space size='large'>
              <Space>
                <InstagramOutlined style={{ fontSize: 20, color: instagramEnabled ? '#e1306c' : '#bbb' }} />
                <Text>Instagram</Text>
                <Switch checked={instagramEnabled} onChange={setInstagramEnabled} />
              </Space>
              <Space>
                <VideoCameraOutlined style={{ fontSize: 20, color: tiktokEnabled ? '#010101' : '#bbb' }} />
                <Text>TikTok</Text>
                <Switch checked={tiktokEnabled} onChange={setTiktokEnabled} />
              </Space>
            </Space>

            <Button
              type='primary'
              size='large'
              icon={<ThunderboltOutlined />}
              loading={isLoading}
              disabled={!selectedProduct || (!instagramEnabled && !tiktokEnabled)}
              onClick={handleGenerate}
              block
            >
              {isLoading ? 'Генеруємо контент...' : 'Згенерувати'}
            </Button>
          </Space>
        </Card>
      )}

      {step === 1 && generated && (
        <PostPreview
          post={generated}
          onBack={() => setStep(0)}
          onPublished={(updated) => navigate(Routes.SOCIAL_LIST)}
        />
      )}
    </div>
  );
};

export default SocialCreate;
